/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Path,
  useForm,
  UseFormHandleSubmit,
  UseFormReturn,
} from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery, UseMutateFunction, useMutation, useQuery,
  useQueryClient,
} from "react-query";
import { BlueBirdTheme, Typography, useSnackbar } from "@kitbird/core";
import { AxiosError } from "axios";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import {
  deleteUserPosition,
  getMutationUserDetailByEmail,
  getMutationUserHistory,
  getMutationUserOptions,
  getUserPositions,
  mutateUserPosition,
} from "../../../services/userPositionService/userPositionService";
import { IOptionItem, IOptionItemAutoComplete, MetadataI } from "../../../typings/common";
import {
  IEmployeeMutationPayload,
  IGetUserPositionData,
  IGetUserPositionResponse,
  IResponseHistoryMutation,
  IResponseMstUserPositionData,
  IResponseUser,
  IResponseUserPositionInfo,
} from "../../../services/userPositionService/types";
import { getPools, IGetPoolsResponse } from "../../../services/poolService";
import {
  getMasterDepartment,
  IGetMasterDepartmentResponse,
} from "../../../services/masterDepartmentService";
import { columnOptions } from "./constants";
import { MutationFormFields } from "./components/MutationUser/types";
import { extractInfoFromFormattedString, formatMutationPayload } from "./utils";
import useAuth from "../../../components/Authentication/useAuth";
import { useDebounce } from "../../../hooks";

export interface FilterI {
  department?: string[] | string;
  pool?: string[] | string;
  search?: string;
  sortBy: string;
  sortType: "ASC" | "DSC" | "DEFAULT";
}

type ModalType = "" | "CREATE" | "DELETE" | "EDIT" | "CONFIRMATION_MUTATION"

interface ContextValueI {
  params: FilterI;
  errorUser: boolean;
  setParams: Dispatch<SetStateAction<FilterI>>;
  meta: MetadataI;
  setMeta: Dispatch<SetStateAction<MetadataI>>;
  loadingDelete: boolean;
  userList: object[]
  userPositionPages?: {
    pages: IGetUserPositionData[][];
  };
  isLoadingUser: boolean;
  observerRef: React.RefObject<HTMLTableSectionElement>
  isFetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  dataPools?: IGetPoolsResponse;
  user?: IGetUserPositionData;
  dataDepartments?: IGetMasterDepartmentResponse;
  modal: ModalType;
  setModal: Dispatch<SetStateAction<ModalType>>;
  confirmationModal: boolean;
  setConfirmationModal: Dispatch<SetStateAction<boolean>>;
  selectedRow?: IGetUserPositionData;
  setSelectedRow: Dispatch<SetStateAction<IGetUserPositionData | undefined>>;
  refetchTable: () => void;
  columnOpts: IOptionItem[];
  setColumnOpts: Dispatch<SetStateAction<IOptionItem[]>>;
  hiddenColumn: string[];
  setHiddenColumn: Dispatch<SetStateAction<string[]>>;
  handleDeleteUser: () => void;
  setMoreActionElem: Dispatch<SetStateAction<HTMLElement | null>>;
  moreActionElem: HTMLElement | null;
  sourceUserPositions: {
    department: string;
    pool: string;
    status: string;
  }[];
  userMutationHistory: {
    date: string;
    pool: string;
    details: {
      label: string;
      value: string;
    }[];
  }[];
  hasNextPageUserMutationHistory?: boolean;
  fetchNextPageUserMutationHistory: (options?: FetchNextPageOptions) =>
    Promise<InfiniteQueryObserverResult<IResponseHistoryMutation, unknown>>;
  isFetchingUserMutationHistory: boolean;
  isFetchingNextPageUserMutationHistory: boolean;
  sourceUserDepartmentOptions: IOptionItemAutoComplete[];
  sourceUserPoolOptions: IOptionItem[];
  incomingUserOptions: IOptionItemAutoComplete[];
  mutationForm: UseFormReturn<MutationFormFields>;
  mutationType: string;
  replacementUser: IOptionItemAutoComplete | null;
  allDepartments: IOptionItem[];
  allPools: IOptionItem[];
  targetUserDetail: IResponseUserPositionInfo | undefined;
  handleSubmit: UseFormHandleSubmit<MutationFormFields>;
  handleProceedToConfirmation: () => void;
  handleConfirmAndSubmit: () => void;
  handleSelectDepartment: (selected: IOptionItem) => void;
  handleSelectPool: (selected: IOptionItem) => void;
  onSubmit: (data: MutationFormFields) => void;
  searchParams: URLSearchParams;
  action: string | null;
  optionsPool: IOptionItem[];
  optionsDepartment: IOptionItem[];
  setReplacementUserSearch: Dispatch<SetStateAction<string>>;
  incomingUserPositions: {
    department: string;
    pool: string;
    status: string;
    jobTitle?: string;
  }[];
  hasNextPageIncomingUserOptions: boolean | undefined;
  fetchNextPageIncomingUserOptions: (options?: FetchNextPageOptions) =>
    Promise<InfiniteQueryObserverResult<IResponseUser, unknown>>;
  isLoadingSourceUserDetail: boolean;
  isLoadingIncomingUserDetail: boolean;
  isFetchingIncomingUserOptions: boolean;
  isIdleIncomingUserOptions: boolean;
  replacementUserInput: string;
  setReplacementUserInput: Dispatch<SetStateAction<string>>;
  mutateEmployee: UseMutateFunction<IResponseMstUserPositionData,
    AxiosError<IResponseMstUserPositionData, any>,
    IEmployeeMutationPayload, unknown>;
  isSubmittingMutation: boolean;
}

export const Context = createContext<ContextValueI | undefined>(undefined);
export const useUserPosition = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("useUserPosition must be used within a Provider component");
  }

  return context;
};

export const Provider = ({ children }: PropsWithChildren) => {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const observerRef = useRef<HTMLTableSectionElement>(null);
  const [searchParams] = useSearchParams();
  const [params, setParams] = useState<FilterI>({
    pool: [],
    department: [],
    search: "",
    sortBy: "",
    sortType: "DSC",
  });

  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [selectedRow, setSelectedRow] = useState<IGetUserPositionData | undefined>(undefined);
  const [modal, setModal] = useState<ModalType>("");
  const [columnOpts, setColumnOpts] = useState<IOptionItem[]>(JSON.parse(localStorage.getItem("COLUMN_USER_POSITION") || JSON.stringify(columnOptions)));
  const [hiddenColumn, setHiddenColumn] = useState<string[]>([]);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [moreActionElem, setMoreActionElem] = useState<HTMLElement | null>(null);
  const [replacementUserSearch, setReplacementUserSearch] = useState("");
  const [replacementUserInput, setReplacementUserInput] = useState("");
  const action = searchParams.get("action");
  const queryClient = useQueryClient();

  const mutationForm = useForm<MutationFormFields>({
    defaultValues: {
      type_mutation: "",
      departments: [],
      pools: [],
      date: "",
      replacement_user: null,
      reason: "",
    },
  });

  const {
    handleSubmit, watch, setValue, reset,
  } = mutationForm;

  const mutationType = watch("type_mutation");
  const replacementUser = watch("replacement_user");
  const allDepartments = watch("departments");
  const allPools = watch("pools");

  useEffect(() => {
    let isAllHidden = true;
    const hiddenCol = columnOpts.map((opt) => {
      let val = "";
      if (!opt.checked) val = opt.value.toString();
      else isAllHidden = false;
      return val;
    });
    if (isAllHidden) {
      setColumnOpts(columnOptions);
      localStorage.setItem("COLUMN_USER_POSITION", JSON.stringify(columnOptions));
      setHiddenColumn([]);
    } else {
      localStorage.setItem("COLUMN_USER_POSITION", JSON.stringify(columnOpts));
      setHiddenColumn(hiddenCol);
    }
  }, [columnOpts]);

  // eslint-disable-next-line arrow-body-style
  const fetchList = async (pageParam: number) => {
    return getUserPositions({
      department: params.department && params.department.length > 0 ? (params.department as string[]).join(",") : undefined,
      pool_id: params.pool && params.pool.length > 0 ? (params.pool as string[]).join(",") : undefined,
      search: params.search,
      page_number: pageParam,
      page_size: 10,
    })
      .then((response) => {
        const res = response as IGetUserPositionResponse;
        if (res.metadata) setMeta(res.metadata);
        return res.lstmstuserpostition;
      })
      .catch(() => []);
  };

  const {
    data: userPositionPages = { pages: [] },
    isFetching,
    isFetchingNextPage,
    refetch: refetchTable,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    ["userPositionList", params],
    async ({ pageParam = 1 }) => fetchList(pageParam),
    {
      getNextPageParam: () => (meta.page < meta.total_pages ? meta.page + 1 : undefined),
      keepPreviousData: true,
    },
  );

  const {
    data: user,
    isLoading: isLoadingUser,
    isError: errorUser,
  } = useQuery({
    queryKey: ["user_data", selectedRow],
    queryFn: () => getUserPositions({ id: selectedRow?.id as number }),
    enabled: !!selectedRow,
  });

  const options = {
    root: observerRef.current,
    rootMargin: "0px",
    threshold: [0],
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(({
      isIntersecting,
    }) => {
      if (isIntersecting && !isFetching) {
        fetchNextPage();
      } else if (meta.page === meta.total_pages && !isFetching) {
        fetchNextPage();
      }
    });
  }, options);

  useEffect(() => {
    if (meta.page === meta.total_pages) observer.unobserve(observerRef.current || document.createElement("tfoot"));
    else observer.observe(observerRef.current || document.createElement("tfoot"));
  }, [meta]);

  const { data: dataPools } = useQuery({
    queryKey: "get_pools",
    queryFn: getPools,
  });

  const { data: dataDepartments } = useQuery({
    queryKey: "get_departments",
    queryFn: getMasterDepartment,
  });

  const userList = useMemo(
    () => userPositionPages.pages.flatMap((page) => page),
    [userPositionPages, params],
  );

  const userDelete = useMutation({
    mutationFn: deleteUserPosition,
    onSuccess: (res) => {
      if (res.responseCode === "Succeed") {
        snackbar.show("Sukses menghapus user", "success");
        setModal("");
        refetchTable();
        return;
      }
      snackbar.show(`${res.responseMessage}`, "error");
      setModal("");
      setSelectedRow(undefined);
    },
    onError: () => {
      snackbar.show("Gagal menghapus user", "error");
      setSelectedRow(undefined);
    },
  });

  const handleDeleteUser = () => {
    const selectedUser = {
      id: selectedRow?.id as number,
      employee_id: selectedRow?.employee_id as string,
      update_by: "ADMIN",
    };
    userDelete.mutate(selectedUser);
  };

  const optionsPool = useMemo(() => {
    if (dataPools?.list_pool) {
      const tempOpt = dataPools.list_pool.map((item) => ({
        label: `${item.pool_id} - ${item.pool_name}`,
        value: item.pool_id,
        checked: params.pool?.includes(item.pool_id),
      }));

      return tempOpt;
    }

    return [];
  }, [dataPools, params.pool]);

  const optionsDepartment = useMemo(() => {
    if (dataDepartments?.lstmstdepartment) {
      const tempOpt = dataDepartments.lstmstdepartment.map((item) => ({
        label: item.department,
        value: item.department,
        checked: params.department?.includes(item.department),
      }));

      return tempOpt;
    }

    return [];
  }, [dataDepartments, params.department]);

  const handleSelectDepartment = (selected: IOptionItem) => {
    const updatedDepartments = allDepartments.map((dept) => (dept.value === selected.value
      ? { ...dept, checked: !dept.checked }
      : dept));
    setValue("departments", updatedDepartments, { shouldDirty: true, shouldValidate: true });
  };

  const handleSelectPool = (selected: IOptionItem) => {
    const updatedPools = allPools.map((dept) => (dept.value === selected.value
      ? { ...dept, checked: !dept.checked }
      : dept));
    setValue("pools", updatedPools, { shouldDirty: true, shouldValidate: true });
  };

  const {
    data: rawSourceUserDetail,
    isLoading: isLoadingSourceUserDetail,
  } = useQuery({
    queryKey: ["source_user_detail", selectedRow],
    queryFn: () => getMutationUserDetailByEmail(
      String(selectedRow?.employee_email),
    ),
    enabled: !!selectedRow && action === "mutation",
  });

  const debouncedReplacementUserSearch = useDebounce(extractInfoFromFormattedString(replacementUserSearch, "email"), 500);

  const {
    data: rawIncomingUserOptions,
    hasNextPage: hasNextPageIncomingUserOptions,
    fetchNextPage: fetchNextPageIncomingUserOptions,
    isFetching: isFetchingIncomingUserOptions,
    isIdle: isIdleIncomingUserOptions,
  } = useInfiniteQuery({
    queryKey: ["incoming_user_options", selectedRow, debouncedReplacementUserSearch],
    queryFn: ({ pageParam = 1 }) => getMutationUserOptions({
      page_number: Number(pageParam),
      search: extractInfoFromFormattedString(debouncedReplacementUserSearch, "email"),
      job_title: user ? (user as IGetUserPositionData).job_title : "",
    }),
    getNextPageParam: (lastPage) => (lastPage.metadata.page < lastPage.metadata.total_pages
      ? lastPage.metadata.page + 1 : undefined),
    enabled: !!selectedRow && action === "mutation" && debouncedReplacementUserSearch.length >= 3,
  });

  const {
    data: rawIncomingUserDetail,
    isLoading: isLoadingIncomingUserDetail,
  } = useQuery({
    queryKey: ["incoming_user_detail", replacementUser],
    queryFn: () => getMutationUserDetailByEmail(
      String(replacementUser?.value),
    ),
    enabled: !!replacementUser,
  });

  const sourceUserPositions = useMemo(() => {
    if (rawSourceUserDetail) {
      const tempOpt: {
        department: string; pool: string; status: string;
      }[] = rawSourceUserDetail.position_info.map(({
        department, pool_name: pool, is_active: isActive,
      }) => ({
        department,
        pool,
        status: isActive ? "Aktif" : "Tidak aktif",
      }));
      return tempOpt;
    }
    return [];
  }, [rawSourceUserDetail]);

  const sourceUserPoolOptions = useMemo(() => {
    if (rawSourceUserDetail) {
      const uniquePoolsMap = rawSourceUserDetail.position_info.reduce(
        (acc, item) => {
          if (!acc.has(item.pool_id)) {
            acc.set(item.pool_id, {
              label: item.pool_name,
              value: `${item.area_id}.${item.pool_id}`,
              checked: false,
            });
          }
          return acc;
        },
        new Map<string, IOptionItem>(),
      );
      return Array.from(uniquePoolsMap.values());
    }
    return [];
  }, [rawSourceUserDetail]);

  const sourceUserDepartmentOptions = useMemo(() => {
    if (rawSourceUserDetail) {
      return rawSourceUserDetail.position_info.map((item) => ({
        label: `${item.department}\n Pool: ${item.pool_name}`,
        labelOverridden: `${item.department} • ${item.pool_id}`,
        value: `${item.pool_id}.${item.department}`,
        checked: false,
      }));
    }
    return [];
  }, [rawSourceUserDetail]);

  const incomingUserOptions = useMemo(() => {
    if (!rawIncomingUserOptions) return [];
    return rawIncomingUserOptions.pages.flatMap((page) => page.user.map((item) => ({
      label: `${item.employee_id} • ${item.employee_name} (${item.employee_email})`,
      value: item.employee_email,
    })));
  }, [rawIncomingUserOptions]);

  const incomingUserPositions = useMemo(() => {
    if (rawIncomingUserDetail) {
      const tempOpt: {
        department: string; pool: string; status: string; jobTitle?: string;
      }[] = rawIncomingUserDetail.position_info.map(({
        department, pool_name: pool, is_active: isActive,
        job_title: jobTitle,
      }) => ({
        department,
        pool,
        status: isActive ? "Aktif" : "Tidak aktif",
        jobTitle,
      }));
      return tempOpt;
    }
    return [];
  }, [rawIncomingUserDetail]);

  const {
    mutate: mutateEmployee,
    isLoading: isSubmittingMutation,
  } = useMutation<IResponseMstUserPositionData,
    AxiosError<IResponseMstUserPositionData>, IEmployeeMutationPayload
  >({
    mutationFn: mutateUserPosition,
    onSuccess: (data) => {
      if (data.responseCode === "Failed") snackbar.show(data.responseMessage, "error");
      else {
        setModal("");
        snackbar.show("Berhasil edit data", "success");
        navigate("/master-data/user-position", { state: null });
        reset();
      }
    },
    onError: (error) => snackbar.show(error?.response?.data.responseCode || "Terjadi kesalahan", "error"),
  });

  const {
    data: rawUserMutationHistory,
    hasNextPage: hasNextPageUserMutationHistory,
    fetchNextPage: fetchNextPageUserMutationHistory,
    isFetching: isFetchingUserMutationHistory,
    isFetchingNextPage: isFetchingNextPageUserMutationHistory,
  } = useInfiniteQuery({
    queryKey: ["user_mutation_history", selectedRow],
    queryFn: ({ pageParam = 1 }) => getMutationUserHistory({
      employee_id: String(selectedRow?.employee_id),
      employee_email: String(selectedRow?.employee_email),
      page_number: Number(pageParam),
    }),
    getNextPageParam: (lastPage) => (lastPage.metadata
      ? lastPage.metadata.page < lastPage.metadata.total_pages
        ? lastPage.metadata.page + 1
        : undefined
      : undefined),
    enabled: !!selectedRow && action === "history",
  });

  const userMutationHistory = useMemo(() => {
    if (!rawUserMutationHistory) return [];
    return rawUserMutationHistory.pages.flatMap((page) => page.list_history_mutasi
      .map((history) => ({
        date: format(new Date(history.tanggal_mutasi), "dd MMMM yyyy", {
          locale: id,
        }),
        pool: history.pool_id,
        details: [
          { label: "Jabatan", value: history.job_title },
          { label: "Departemen", value: history.department },
        ],
      })));
  }, [rawUserMutationHistory]);

  const onSubmit = (data: MutationFormFields) => {
    if (!user) return;
    const targetUser = user as IGetUserPositionData;
    const formData = {
      ...data,
      targetUser,
      departments: data.departments.filter((d) => d.checked),
      pools: data.pools.filter((p) => p.checked),
      create_by: userInfo?.preferred_username || "",
      incomingJobTitle: rawIncomingUserDetail?.position_info.length ? rawIncomingUserDetail.position_info[0].job_title : "",
    };
    mutateEmployee(formatMutationPayload(formData));
  };

  const handleProceedToConfirmation = () => handleSubmit(() => setModal("CONFIRMATION_MUTATION"))();
  const handleConfirmAndSubmit = () => handleSubmit(onSubmit)();

  const resetState = () => {
    reset();
    setSelectedRow(undefined);
    setModal("");
    setConfirmationModal(false);
    setReplacementUserSearch("");
    setReplacementUserInput("");
    queryClient.invalidateQueries();
  };

  useEffect(() => {
    reset({
      type_mutation: "",
      departments: sourceUserDepartmentOptions,
      pools: sourceUserPoolOptions,
      date: "",
      replacement_user: null,
      reason: "",
    });
  }, [reset, sourceUserDepartmentOptions, sourceUserPoolOptions]);

  useEffect(() => {
    const newValue = {
      pool: ["departments", sourceUserDepartmentOptions],
      department: ["pools", sourceUserPoolOptions],
    }[mutationType as "pool" | "department"];
    if (newValue) {
      setValue(newValue[0] as Path<MutationFormFields>, newValue[1]);
    }
  }, [mutationType, setValue, sourceUserDepartmentOptions, sourceUserPoolOptions]);

  useEffect(() => {
    if (!action) resetState();
  }, [action]);

  return (
    <Context.Provider
      value={{
        params,
        setParams,
        meta,
        setMeta,
        hasNextPage,
        userPositionPages,
        isFetching,
        isFetchingNextPage,
        userList,
        dataPools,
        dataDepartments,
        errorUser,
        modal,
        refetchTable,
        setModal,
        user: (user as IGetUserPositionData),
        isLoadingUser,
        selectedRow,
        setSelectedRow,
        columnOpts,
        confirmationModal,
        setConfirmationModal,
        setColumnOpts,
        hiddenColumn,
        setHiddenColumn,
        loadingDelete: userDelete.isLoading,
        observerRef,
        handleDeleteUser,
        moreActionElem,
        setMoreActionElem,
        hasNextPageUserMutationHistory,
        fetchNextPageUserMutationHistory,
        isFetchingUserMutationHistory,
        isFetchingNextPageUserMutationHistory,
        userMutationHistory,
        sourceUserPositions,
        sourceUserDepartmentOptions,
        sourceUserPoolOptions,
        incomingUserOptions,
        mutationForm,
        mutationType,
        replacementUser,
        allDepartments,
        allPools,
        targetUserDetail: rawIncomingUserDetail,
        handleSubmit,
        handleProceedToConfirmation,
        handleConfirmAndSubmit,
        handleSelectDepartment,
        handleSelectPool,
        onSubmit,
        searchParams,
        action,
        optionsPool,
        optionsDepartment,
        setReplacementUserSearch,
        incomingUserPositions,
        hasNextPageIncomingUserOptions,
        fetchNextPageIncomingUserOptions,
        isLoadingSourceUserDetail,
        isLoadingIncomingUserDetail,
        isFetchingIncomingUserOptions,
        isIdleIncomingUserOptions,
        replacementUserInput,
        setReplacementUserInput,
        mutateEmployee,
        isSubmittingMutation,
      }}
    >
      {children}
    </Context.Provider>
  );
};
